import React, { useState, useEffect } from 'react';
import { apiFetch, getApiBaseUrl } from '../../../utils/apiConfig';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Alert,
  Divider,
  Chip,
  Grid,
  IconButton,
  Tooltip
} from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getAuthHeaders } from '../../../utils/authUtils';


const PACS_PATH = '%jcs_home%/cfg/';

const PacsMappingDialog = ({ open, onClose, mapping = null, onSave }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: mapping?.name || '',
    description: mapping?.description || '',
    source_system: 'pacs',
    is_active: mapping?.is_active ?? true,
    pacs_config_file: mapping?.pacs_config_file || '',
    accession_number_field: mapping?.accession_number_field || '',
    study_instance_uid_field: mapping?.study_instance_uid_field || ''
  });
  const [fileContent, setFileContent] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (mapping) {
      setFormData({
        name: mapping.name || '',
        description: mapping.description || '',
        source_system: 'pacs',
        is_active: mapping.is_active ?? true,
        pacs_config_file: mapping.pacs_config_file || '',
        accession_number_field: mapping.accession_number_field || '',
        study_instance_uid_field: mapping.study_instance_uid_field || ''
      });
      setFileContent(mapping.pacs_config_file || '');
    } else {
      setFormData({
        name: '',
        description: '',
        source_system: 'pacs',
        is_active: true,
        pacs_config_file: '',
        accession_number_field: '',
        study_instance_uid_field: ''
      });
      setFileContent('');
    }
    setValidationError('');
  }, [mapping, open]);

  const handleFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setFileContent(content);
      setFormData(prev => ({
        ...prev,
        pacs_config_file: content
      }));
      extractFieldsFromXML(content);
    };
    reader.readAsText(file);
  };

  const extractFieldsFromXML = (xmlContent) => {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
      const obrSegments = xmlDoc.querySelectorAll('HL7Segment[name="OBR"]');
      for (const obrSegment of obrSegments) {
        const accessionFields = obrSegment.querySelectorAll('HL7FieldDicom[description="accessionNumber"]');
        for (const field of accessionFields) {
          const fieldIndex = field.getAttribute('fieldIndex');
          if (fieldIndex) {
            setFormData(prev => ({
              ...prev,
              accession_number_field: `OBR.${fieldIndex}`
            }));
            break;
          }
        }
      }
      const obxSegments = xmlDoc.querySelectorAll('HL7Segment[name="OBX"]');
      for (const obxSegment of obxSegments) {
        const studyUidFields = obxSegment.querySelectorAll('HL7FieldDicom[description="Dicom Study Instance UID"]');
        for (const field of studyUidFields) {
          const fieldIndex = field.getAttribute('fieldIndex');
          if (fieldIndex) {
            setFormData(prev => ({
              ...prev,
              study_instance_uid_field: `OBX.${fieldIndex}`
            }));
            break;
          }
        }
      }
      setValidationError('');
    } catch (error) {
      console.error('Fehler beim Parsen der XML:', error);
      setValidationError('Fehler beim Parsen der XML-Datei');
    }
  };

  const handleCopyPath = () => {
    navigator.clipboard.writeText(PACS_PATH);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setValidationError('');

    try {
      if (!formData.name.trim()) {
        setValidationError('Name ist erforderlich');
        return;
      }
      if (!formData.pacs_config_file.trim()) {
        setValidationError('PACS-Konfigurationsdatei ist erforderlich');
        return;
      }
      if (!formData.accession_number_field.trim()) {
        setValidationError('Accession Number Feld ist erforderlich');
        return;
      }
      if (!formData.study_instance_uid_field.trim()) {
        setValidationError('Study Instance UID Feld ist erforderlich');
        return;
      }
      const url = mapping 
        ? `${getApiBaseUrl()}/admin/hl7-mappings/${mapping.id}`
        : `${getApiBaseUrl()}/admin/hl7-mappings`;
      const method = mapping ? 'PUT' : 'POST';
      // Für PACS-Mappings: mapping_rules leer lassen und PACS-Felder korrekt setzen
      const pacsMappingData = {
        name: formData.name,
        description: formData.description,
        source_system: 'pacs',
        is_active: formData.is_active,
        mapping_rules: {}, // Leere mapping_rules für PACS
        sample_message: '', // Leere sample_message für PACS
        pacs_config_file: formData.pacs_config_file,
        accession_number_field: formData.accession_number_field,
        study_instance_uid_field: formData.study_instance_uid_field
      };
      
      console.log('Sending PACS mapping data:', pacsMappingData);
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(pacsMappingData)
      });
      if (response.ok) {
        // Erfolgreich gespeichert - Dialog schließen und Liste aktualisieren
        onClose();
        onSave();
      } else {
        const errorData = await response.json();
        setValidationError(errorData.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      setValidationError('Netzwerkfehler beim Speichern');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      source_system: 'pacs',
      is_active: true,
      pacs_config_file: '',
      accession_number_field: '',
      study_instance_uid_field: ''
    });
    setFileContent('');
    setValidationError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mapping ? t('admin.hl7Mapping.editPacsMapping') : t('admin.hl7Mapping.addPacsMapping')}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {validationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              name="name"
              label={t('admin.hl7Mapping.name')}
              value={formData.name}
              onChange={handleFormChange}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              name="description"
              label={t('admin.hl7Mapping.description')}
              value={formData.description}
              onChange={handleFormChange}
              multiline
              rows={2}
              sx={{ mb: 3 }}
            />
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('admin.hl7Mapping.pacsConfigFileTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              {t('admin.hl7Mapping.pacsConfigFileHint1')}
              <span style={{ fontWeight: 500 }}>{t('admin.hl7Mapping.pacsConfigFilePath', { path: PACS_PATH })}</span>
              <Tooltip title={copied ? t('admin.hl7Mapping.copied') : t('admin.hl7Mapping.copyToClipboard')}>
                <IconButton size="small" onClick={handleCopyPath} aria-label="Pfad kopieren">
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Typography>
            <input
              accept=".xml"
              style={{ display: 'none' }}
              id="pacs-config-file"
              type="file"
              onChange={handleFileUpload}
            />
            <label htmlFor="pacs-config-file">
              <Button variant="outlined" component="span" className="button">
                XML-Datei auswählen
              </Button>
            </label>
            {fileContent && (
              <Box sx={{ mt: 2 }}>
                <Chip 
                  label={t('admin.hl7Mapping.fileLoaded')} 
                  color="success" 
                  size="small" 
                  sx={{ mr: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {fileContent.length} Zeichen
                </Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            {t('admin.hl7Mapping.extractedFields')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('admin.hl7Mapping.pacsExtractedFieldsHint')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="accession_number_field"
                label={t('admin.hl7Mapping.accessionNumberField')}
                value={formData.accession_number_field}
                onChange={handleFormChange}
                required
                helperText={t('admin.hl7Mapping.accessionNumberExample')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="study_instance_uid_field"
                label={t('admin.hl7Mapping.studyInstanceUidField')}
                value={formData.study_instance_uid_field}
                onChange={handleFormChange}
                required
                helperText={t('admin.hl7Mapping.studyInstanceUidExample')}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleFormChange}
                  />
                }
                label={t('admin.hl7Mapping.active')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading} className="button-cancel">
            {t('admin.hl7Mapping.cancel')}
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={isLoading}
            className="button"
          >
            {isLoading ? 'Speichern...' : (mapping ? t('admin.hl7Mapping.update') : t('admin.hl7Mapping.save'))}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PacsMappingDialog; 